import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageSeo } from "@/hooks/usePageSeo";
import FilterPanel from "@/pages/category/components/FilterPanel";
import VideoResultList from "@/pages/category/components/VideoResultList";
import { paginate, searchVideos, sortVideos, applyFilters, type SortKey } from "@/lib/videos";

const PAGE_SIZE = 9;
const typeTabs = [
  { value: "all", label: "全部类型" },
  { value: "movie", label: "电影" },
  { value: "short", label: "短视频" },
];

export default function SearchPage({ keyword }: { keyword: string }) {
  const [sort, setSort] = useState<SortKey>("latest");
  const [typeTab, setTypeTab] = useState("all");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreFilters, setMoreFilters] = useState({
    regions: [] as string[],
    years: [] as string[],
    durations: [] as string[],
  });
  const [query, setQuery] = useState(keyword);

  useEffect(() => {
    setQuery(keyword);
    setPage(1);
  }, [keyword]);

  const navigate = useNavigate();

  usePageSeo({
    title: keyword ? `激情视频 - 搜索「${keyword}」结果` : "激情视频 - 全站搜索",
    description: keyword
      ? `在激情视频搜索「${keyword}」，查看相关激情影视与短视频结果，支持按类型、地区、年份、时长筛选。`
      : "在激情视频全站搜索激情影视与短视频，支持关键词检索与多条件筛选。",
    keywords: "激情视频搜索,激情影视搜索,短视频搜索,视频筛选",
  });

  const filters = {
    types: typeTab === "all" ? [] : [typeTab],
    regions: moreFilters.regions,
    years: moreFilters.years,
    durations: moreFilters.durations,
  };

  const results = useMemo(() => {
    const base = searchVideos(keyword);
    return sortVideos(applyFilters(base, filters), sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, typeTab, sort, moreFilters]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = paginate(results, currentPage, PAGE_SIZE);

  const resetAll = () => {
    setTypeTab("all");
    setSort("latest");
    setPage(1);
    setMoreFilters({ regions: [], years: [], durations: [] });
  };

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = query.trim();
    if (value) {
      navigate(`/search?q=${encodeURIComponent(value)}`);
    }
  };

  return (
    <SiteLayout>
      <div className="px-4 md:px-8 lg:px-12 pt-6 md:pt-10">
        <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-8">
          <p className="text-[11px] tracking-[0.24em] text-accent-400 font-label uppercase">Search</p>
          <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold text-foreground-50">
            {keyword ? (
              <>
                搜索「<span className="text-primary-400">{keyword}</span>」
              </>
            ) : (
              "全站搜索"
            )}
          </h1>
          <form onSubmit={submitHandler} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 h-11 px-3 rounded-md bg-background-50 border border-background-300/70 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="输入片名、导演、地区或标签关键词"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-600"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <p className="mt-3 text-xs text-foreground-500">
            共找到 <span className="text-accent-400 font-medium">{results.length}</span> 个相关结果
            {keyword && <span className="text-foreground-600"> · 关键词按片名、简介、导演、演员与标签匹配</span>}
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 mt-6 md:mt-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="hidden lg:block w-[248px] shrink-0">
            <FilterPanel
              filters={{ ...filters, regions: moreFilters.regions, years: moreFilters.years, durations: moreFilters.durations }}
              typeLocked={typeTab !== "all"}
              onTypesChange={(values) => {
                setTypeTab(values[0] ?? "all");
                setPage(1);
              }}
              onRegionsChange={(values) => {
                setMoreFilters((prev) => ({ ...prev, regions: values }));
                setPage(1);
              }}
              onYearsChange={(values) => {
                setMoreFilters((prev) => ({ ...prev, years: values }));
                setPage(1);
              }}
              onDurationsChange={(values) => {
                setMoreFilters((prev) => ({ ...prev, durations: values }));
                setPage(1);
              }}
              onReset={resetAll}
            />
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70 overflow-x-auto no-scrollbar">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => {
                      setTypeTab(tab.value);
                      setPage(1);
                    }}
                    className={`h-8 px-4 rounded-full text-xs transition-colors whitespace-nowrap cursor-pointer ${
                      typeTab === tab.value
                        ? "bg-primary-500 text-background-50 font-medium"
                        : "text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
            </div>

            <VideoResultList
              videos={pageItems}
              sort={sort}
              onSortChange={(value) => {
                setSort(value);
                setPage(1);
              }}
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyTitle={keyword ? "没有找到匹配的作品" : "输入关键词开始搜索"}
              emptyDescription={
                keyword
                  ? "换个关键词试试，或者放宽筛选条件。你也可以从热门主题里找找灵感。"
                  : "试试搜索「午夜」「夜」「音乐」等关键词，查看站点内的相关作品。"
              }
              onReset={resetAll}
              emptyActionTo="/category/theme"
              emptyActionLabel="浏览热门主题"
            />
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-950/70 cursor-pointer"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[86%] max-w-[340px] bg-background-100 overflow-y-auto scrollbar-thin animate-slide-in-right">
            <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/70 sticky top-0 bg-background-100 z-10">
              <span className="text-sm font-medium text-foreground-50">筛选条件</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-300 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4">
              <FilterPanel
                filters={{ ...filters, regions: moreFilters.regions, years: moreFilters.years, durations: moreFilters.durations }}
                typeLocked={typeTab !== "all"}
                onTypesChange={(values) => {
                  setTypeTab(values[0] ?? "all");
                  setPage(1);
                }}
                onRegionsChange={(values) => {
                  setMoreFilters((prev) => ({ ...prev, regions: values }));
                  setPage(1);
                }}
                onYearsChange={(values) => {
                  setMoreFilters((prev) => ({ ...prev, years: values }));
                  setPage(1);
                }}
                onDurationsChange={(values) => {
                  setMoreFilters((prev) => ({ ...prev, durations: values }));
                  setPage(1);
                }}
                onReset={resetAll}
              />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="mt-5 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                查看 {results.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 md:px-8 lg:px-12 mt-10">
        <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-sm text-foreground-400">没找到想要的？也可以直接从分类页开始浏览。</p>
          <Link
            to="/category/movie"
            className="inline-flex items-center gap-1 h-9 px-4 rounded-md bg-secondary-800 text-secondary-100 text-xs hover:bg-secondary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            进入分类页
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}